import type React from 'react';
import { useTranslation } from 'react-i18next';

import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Link, useSearchParams } from '@remix-run/react';
import cx from 'classnames';

import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { HomePage } from '~/interfaces/home_page';

import type { HomePageLoaderData } from '../../routes/index';

import { useWebThemeContext } from '../Layout/Context/useWebThemeContext';

// Utility function to construct URLs with parameters
const appendQueryParam = (baseUrl: string, paramName: string, paramValue: number, searchParams: any) => {
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.delete(paramName); // Delete the parameter if it already exists
  newSearchParams.set(paramName, paramValue.toString()); // Set the new parameter value
  const paramsString = newSearchParams.toString();
  const newPath = paramsString ? `${baseUrl}?${paramsString}` : baseUrl;
  return newPath;
};

interface LinkProps {
  to: string;
  disabled?: boolean | null;
  rel?: string;
  activePage?: boolean;
  children: React.ReactNode | string;
  color?: string;
  borderColor?: string;
  isNewWebBuilder?: boolean;
  primaryColor?: string;
  primaryTextColor?: string;
}

const CustomLink: React.FC<LinkProps> = ({
  to,
  disabled,
  // biome-ignore lint/correctness/noUnusedVariables: Keeping for backwards compatibility
  rel,
  children,
  activePage,
  color,
  borderColor,
  isNewWebBuilder = false,
  primaryColor,
  primaryTextColor,
}) => {
  const { classes } = useWebThemeContext();

  return (
    <Link
      to={to}
      className={cx(
        classes.radius,
        'flex items-center gap-2 border border-transparent px-3 py-1 transition duration-150 ease-in-out hover:border-wt-primary',
        {
          'pointer-events-none cursor-not-allowed opacity-40': disabled,
          'bg-wt-primary text-wt-text-on-primary': activePage && !isNewWebBuilder,
        },
      )}
      style={{
        color: isNewWebBuilder ? primaryTextColor : color,
        border: borderColor && `1px solid ${borderColor}`,
        backgroundColor: isNewWebBuilder ? primaryColor : '',
      }}
    >
      {children}
    </Link>
  );
};

interface PaginationProps {
  homePage?: HomePage;
  isNewWebBuilder?: boolean;
  buttonBGColor: string;
  buttonBorderColor: string;
  buttonTextColor: string;
}

const baseUrl = '../archive';
const linksToShow = 8;
const pageParam = 'page';

const Pagination: React.FC<PaginationProps> = ({
  homePage,
  isNewWebBuilder = false,
  buttonBGColor,
  buttonBorderColor,
  buttonTextColor,
}) => {
  const [searchParams] = useSearchParams();
  const { paginatedPosts } = useTypedLoaderData<HomePageLoaderData>();
  const color = buttonTextColor;
  const borderColor = buttonBorderColor;
  const bgColor = buttonBGColor;
  const activeBgColor = homePage?.posts_section.archive.button_active_bg_color || '';
  const activeTextColor = homePage?.posts_section.archive.button_active_text_color || '';

  const pageNumber = Number.parseInt(searchParams.get('page') || '1', 10); // Convert to a number
  // Number of links to show at a time
  const startLink = Math.max(1, pageNumber - Math.floor(linksToShow / 2));
  const { t } = useTranslation();

  const endLink = Math.min(startLink + linksToShow - 1, paginatedPosts.pagination.total_pages);

  if (paginatedPosts.pagination.total_pages === 1) {
    return null;
  }

  return (
    <div className="col-span-12 mx-auto mt-12 grid grid-cols-4 grid-rows-2 items-center gap-3 px-4 py-4 pb-8 sm:flex sm:justify-center">
      <div className="wt-button-font order-2 col-span-2 flex justify-end gap-2">
        <CustomLink
          to={appendQueryParam(baseUrl, pageParam, 1, searchParams)}
          disabled={pageNumber === 1 ? true : null}
          color={color}
          borderColor={borderColor}
          primaryColor={bgColor}
          primaryTextColor={color}
          isNewWebBuilder={isNewWebBuilder}
        >
          {t('search.pagination.first')}
        </CustomLink>
        <CustomLink
          to={appendQueryParam(baseUrl, pageParam, pageNumber - 1, searchParams)}
          rel="prev"
          disabled={pageNumber === 1}
          color={color}
          primaryColor={bgColor}
          primaryTextColor={color}
          borderColor={borderColor}
          isNewWebBuilder={isNewWebBuilder}
        >
          <ChevronRightIcon className="rotate-180 transform" height="16px" />
          {t('search.pagination.back')}
        </CustomLink>
      </div>
      <div className="order-1 col-span-4 flex justify-center gap-3 sm:order-2">
        {Array.from({ length: endLink - startLink + 1 }, (_, index) => {
          const pageIndex = startLink + index;
          const isActive = pageNumber === pageIndex;
          const bgColorToUse = isActive ? activeBgColor : bgColor;
          const textColorToUse = isActive ? activeTextColor : color;

          return (
            <CustomLink
              key={index}
              to={appendQueryParam(baseUrl, pageParam, pageIndex, searchParams)}
              activePage={isActive}
              color={textColorToUse}
              borderColor={borderColor}
              primaryColor={bgColorToUse}
              primaryTextColor={textColorToUse}
              isNewWebBuilder={isNewWebBuilder}
            >
              {pageIndex}
            </CustomLink>
          );
        })}
      </div>
      <div className="wt-button-font order-2 col-span-2 flex items-center gap-2">
        <CustomLink
          to={appendQueryParam(baseUrl, pageParam, pageNumber + 1, searchParams)}
          disabled={pageNumber === paginatedPosts.pagination.total_pages}
          rel="next"
          color={color}
          primaryColor={bgColor}
          primaryTextColor={color}
          borderColor={borderColor}
          isNewWebBuilder={isNewWebBuilder}
        >
          {t('search.pagination.next')} <ChevronRightIcon height="16px" />
        </CustomLink>
        <CustomLink
          to={appendQueryParam(baseUrl, pageParam, paginatedPosts.pagination.total_pages, searchParams)}
          borderColor={borderColor}
          primaryColor={bgColor}
          primaryTextColor={color}
          disabled={pageNumber === paginatedPosts.pagination.total_pages ? true : null}
          color={color}
          isNewWebBuilder={isNewWebBuilder}
        >
          {t('search.pagination.last')}
        </CustomLink>
      </div>
    </div>
  );
};

export default Pagination;
